import yup from "../../../../../utils/yup-extended";
import {yupResolver} from "@hookform/resolvers/yup";
import {Controller, FormProvider, useForm, useFormContext} from "react-hook-form";
import {Alert, Breadcrumb, Button, Input, message, Space, Tooltip} from "antd";
import {
  StyledCardCTAButtonWrapper,
  StyleDrawerInfoBlock,
  StyledUpcomingCallCard,
  StyledUpcomingCallCardMetaInfo,
} from "./styles";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DollarOutlined,
  HourglassOutlined,
  NumberOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {ReactNode, useState} from "react";
import {
  CoachCancelUpcomingCallPayload,
  CustomerCancelUpcomingCallPayload,
  GetCoachUpcomingCallsApiResponseTransaction,
} from "../../../../../react-query-hooks/session/upcoming-calls/types";
import {useGetCustomerBookingMutation} from "../../../../../react-query-hooks/booking";
import {useGetPublicCoachProfile} from "../../../../../react-query-hooks/public-profile";
import {TFetchCustomerBookingPayload} from "../../../../../redux/booking/i-time-slots-payload";
import {handleMeetingLink} from "../../../../../utils/meeting";
import {RootState} from "../../../../../redux/store";
import {useSelector} from "react-redux";
import {StyledSessionDetailsDrawer} from "../../../service/sales-and-insights/customers-section/style";
import {
  SessionDetailsDrawerBookingQuestions,
  SessionDetailsDrawerBookingQuestionsProps,
} from "../../../service/sales-and-insights/customers-section/booking-questions";
import {FormField, FormFieldError} from "../../../../../design-system/form-field";
import {
  useCoachCancelUpcomingCall,
  useCustomerCancelUpcomingCall,
} from "../../../../../react-query-hooks/session/upcoming-calls";
import {resolveGoogleMeetUrl} from "../../../../../react-query-hooks/booking/booking-join-normalizer";
import {FullPageLoader} from "../../../../full-page-loader";
import {CoachRescheduleCallCalendar} from "../call-card-menu/reschedule-call-modal/calendar";
import {
  BookingProvider,
  useBookingContext,
} from "../../../../../context/booking/booking-context";
import {CustomerRescheduleCallCalendar} from "../call-card-menu/reschedule-call-modal/calendar/customer";
const {TextArea} = Input;

type CoachUpcomingCallCardProps = {
  transaction: GetCoachUpcomingCallsApiResponseTransaction;
};
type CoachUpcomingCallJoinVideoCallButtonProps = {
  bookingType: GetCoachUpcomingCallsApiResponseTransaction["booking_type"];
  phoneNumber: GetCoachUpcomingCallsApiResponseTransaction["phone_number"];
  reference: GetCoachUpcomingCallsApiResponseTransaction["reference"];
  roomId: GetCoachUpcomingCallsApiResponseTransaction["roomId"];
  serviceName: GetCoachUpcomingCallsApiResponseTransaction["service"]["name"];
  videoPlatform: GetCoachUpcomingCallsApiResponseTransaction["video_platform"];
};

type CoachUpcomingCallViewDetailsButtonProps = {
  showViewDetailsDrawer: () => void;
};

type UpcomingCallCardViewDetailsDrawerProps = {
  title: string;
  onClose: () => void;
  showCancelDrawer: () => void;
  showRescheduleCallDrawer: () => void;
  isVisible: boolean;
  checkoutFormResponses: SessionDetailsDrawerBookingQuestionsProps["data"];
  callMetaInfo: UpcomingCallCardMetaInfoProps["data"];
  callLocation: GetCoachUpcomingCallsApiResponseTransaction["booking_type"];
  videoPlatform: GetCoachUpcomingCallsApiResponseTransaction["video_platform"];
  emailInfo: {
    emailText: string;
    emailValue: string;
  };
  redirectUrl: {
    href: string;
    name: string;
  } | null;
};
type UpcomingCallCardCancelCallDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  nameOfPerson: string;
  bookingReference: GetCoachUpcomingCallsApiResponseTransaction["reference"];
  type: "coach" | "customer";
};
type UpcomingCallCardRescheduleCallDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  nameOfPerson: string;
  bookingReference: GetCoachUpcomingCallsApiResponseTransaction["reference"];
  /** From the upcoming-calls list item. */
  serviceId: GetCoachUpcomingCallsApiResponseTransaction["serviceId"];
  /**
   * The coach's timezone, taken from the upcoming-calls list item. Drives the
   * availability calendar's slot conversion.
   */
  mentorTimezone: GetCoachUpcomingCallsApiResponseTransaction["mentor_timezone"];
  /**
   * Customer dashboard only: names the coach so their numeric id can be looked
   * up. The coach dashboard reads its own id off the session instead.
   */
  coachUsername?: string;
  type: "coach" | "customer";
};

type DrawerInfoBlockProps = {
  nameOfPerson: UpcomingCallCardCancelCallDrawerProps["nameOfPerson"];
};

type UpcomingCallCardCancelCallDrawerCancelFormProps = {
  closeCancelCallDrawer: () => void;
  bookingReference: GetCoachUpcomingCallsApiResponseTransaction["reference"];
  type: UpcomingCallCardRescheduleCallDrawerProps["type"];
};

export type UpcomingCallCardMetaInfoProps = {
  data: {
    icon: ReactNode;
    value: string;
  }[];
};

export const cancelFormSchema = yup.object().shape({
  message: yup.string().trim(),
});
export const rescheduleFormSchema = yup.object().shape({
  message: yup.string().trim(),
});

export type CancelCallFormValues = {
  message: string;
};
export type RescheduleCallFormValues = {
  message: string;
};

export function CoachUpcomingCallCard({
  transaction: {
    amount,
    booking_date,
    booking_month,
    booking_year,
    booking_type,
    bookingQuestions,
    bundle_session,
    coach_from_time,
    currency,
    email_address,
    first_name,
    is_bundle,
    last_name,
    phone_number,
    timeslot,
    reference,
    roomId,
    videoPlatform,
    serviceId,
    mentor_timezone,
    service: {callDuration, no_of_sessions, name},
  },
}: CoachUpcomingCallCardProps) {
  const [isViewDetailsDrawerOpen, setIsViewDetailsDrawerOpen] = useState<boolean>(false);
  const [isCancelCallDrawerOpen, setIsCancelCallDrawerOpen] = useState<boolean>(false);
  const [isRescheduleCallDrawerOpen, setIsRescheduleCallDrawerOpen] =
    useState<boolean>(false);
  const cancelCallFormMethods = useForm<CancelCallFormValues>({
    resolver: yupResolver(cancelFormSchema),
    mode: "onChange",
    defaultValues: {
      message: "",
    },
  });
  const rescheduleCallFormMethods = useForm<RescheduleCallFormValues>({
    resolver: yupResolver(rescheduleFormSchema),
    mode: "onChange",
    defaultValues: {
      message: "",
    },
  });
  const cardType = "coach";
  const callAmount = amount === 0 ? "Free" : `${currency} ${amount.toLocaleString()}`;
  const callDate = new Date(
    Number(booking_year),
    Number(booking_month) - 1,
    Number(booking_date)
  ).toDateString();
  const callTime = coach_from_time || timeslot;
  const callDurationText = `${callDuration} minutes`;
  const callLocationIcon =
    booking_type === "video" ? (
      <VideoCameraOutlined style={{color: "#222222"}} />
    ) : (
      <PhoneOutlined style={{color: "#222222"}} />
    );
  const bundleSessions = is_bundle
    ? `| ${bundle_session} of ${no_of_sessions} sessions`
    : "";
  const callLocationText: string =
    booking_type !== "video"
      ? `Phone call ${bundleSessions}`
      : videoPlatform === "googleMeet"
      ? `Google Meet ${bundleSessions}`
      : videoPlatform === "zoom"
      ? `Zoom ${bundleSessions}`
      : `Video call ${bundleSessions}`;

  const cardTitle = `${name} with ${first_name} ${last_name}`;
  function generateCallCardMetaInfo() {
    const baseMetaInfo = [
      {
        icon: <DollarOutlined style={{color: "#222222"}} />,
        value: callAmount,
      },
      {
        icon: <CalendarOutlined style={{color: "#222222"}} />,
        value: callDate,
      },
      {
        icon: <ClockCircleOutlined style={{color: "#222222"}} />,
        value: callTime,
      },
      {
        icon: <HourglassOutlined style={{color: "#222222"}} />,
        value: callDurationText,
      },
      {
        icon: callLocationIcon,
        value: callLocationText,
      },
    ];

    if (booking_type === "audio") {
      return baseMetaInfo.concat([
        {
          icon: <NumberOutlined style={{color: "#222222"}} />,
          value: `${phone_number}`,
        },
      ]);
    } else {
      return baseMetaInfo;
    }
  }
  const callCardMetaInfo = generateCallCardMetaInfo();
  const checkoutFormResponses =
    (JSON.parse(bookingQuestions) as SessionDetailsDrawerBookingQuestionsProps["data"]) ||
    [];

  const showViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(true);
  };
  const closeViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(false);
  };
  const showCancelCallDrawer = () => {
    setIsCancelCallDrawerOpen(true);
  };
  const closeCancelCallDrawer = () => {
    setIsCancelCallDrawerOpen(false);
    cancelCallFormMethods.reset();
  };
  const showRescheduleCallDrawer = () => {
    setIsRescheduleCallDrawerOpen(true);
  };
  const closeRescheduleCallDrawer = () => {
    setIsRescheduleCallDrawerOpen(false);
    rescheduleCallFormMethods.reset();
  };

  return (
    <StyledUpcomingCallCard title={cardTitle}>
      <Space direction="vertical" size={16} style={{display: "flex"}}>
        <UpcomingCallCardMetaInfo data={callCardMetaInfo} />
        <StyledCardCTAButtonWrapper>
          <CoachUpcomingCallJoinVideoCallButton
            bookingType={booking_type}
            phoneNumber={phone_number}
            reference={reference}
            roomId={roomId}
            serviceName={name}
            videoPlatform={videoPlatform}
          />
          <CoachUpcomingCallViewDetailsButton
            showViewDetailsDrawer={showViewDetailsDrawer}
          />
        </StyledCardCTAButtonWrapper>
      </Space>
      <UpcomingCallCardViewDetailsDrawer
        isVisible={isViewDetailsDrawerOpen}
        onClose={closeViewDetailsDrawer}
        title={cardTitle}
        checkoutFormResponses={checkoutFormResponses}
        callMetaInfo={callCardMetaInfo}
        callLocation={booking_type}
        videoPlatform={videoPlatform}
        emailInfo={{
          emailText: "Customer's email",
          emailValue: email_address,
        }}
        showCancelDrawer={showCancelCallDrawer}
        showRescheduleCallDrawer={showRescheduleCallDrawer}
        redirectUrl={null}
      />
      <FormProvider {...cancelCallFormMethods}>
        <UpcomingCallCardCancelCallDrawer
          isVisible={isCancelCallDrawerOpen}
          nameOfPerson={`${first_name} ${last_name}`}
          onClose={closeCancelCallDrawer}
          title={cardTitle}
          bookingReference={reference}
          type={cardType}
        />
      </FormProvider>
      {isRescheduleCallDrawerOpen && (
        <FormProvider {...rescheduleCallFormMethods}>
          <BookingProvider>
            <UpcomingCallCardRescheduleCallDrawer
              isVisible={isRescheduleCallDrawerOpen}
              nameOfPerson={`${first_name} ${last_name}`}
              onClose={closeRescheduleCallDrawer}
              title={cardTitle}
              bookingReference={reference}
              serviceId={serviceId}
              mentorTimezone={mentor_timezone}
              type={cardType}
            />
          </BookingProvider>
        </FormProvider>
      )}
    </StyledUpcomingCallCard>
  );
}

const MEET_POLL_INTERVAL_MS = 2000;
const MEET_POLL_MAX_ATTEMPTS = 15;

function isGoogleMeetLink(roomId?: string | null) {
  return !!roomId && /meet\.google\.com/i.test(roomId);
}

export function CoachUpcomingCallJoinVideoCallButton({
  bookingType,
  phoneNumber,
  reference,
  roomId,
  serviceName,
  videoPlatform,
}: CoachUpcomingCallJoinVideoCallButtonProps) {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const getCustomerBookingQueryMutation = useGetCustomerBookingMutation();
  const [isJoining, setIsJoining] = useState(false);
  const toolTipTitle = bookingType === "video" ? "Join session" : "Copy phone number";
  const buttonText = bookingType === "video" ? "Join video call" : "Copy phone number";

  function copyPhoneNumber() {
    navigator.clipboard.writeText(`${phoneNumber}`).then(() => message.success("Copied"));
  }

  function attemptJoin(
    userType: "host_user" | "guest_user",
    role: "host" | "guest",
    attempt: number
  ) {
    const payload: TFetchCustomerBookingPayload = {reference, userType};
    getCustomerBookingQueryMutation.mutate(payload, {
      onSuccess: (data) => {
        const platform = data.data.videoPlatform.toLowerCase();
        if (platform === "dailyco") {
          setIsJoining(false);
          window.open(`${data.data.dailyCoUrl}?t=${data.data.bookingToken}`);
        } else if (platform === "googlemeet") {
          const meetUrl = resolveGoogleMeetUrl(data, roomId);
          if (meetUrl) {
            message.destroy("meet-link-loading");
            setIsJoining(false);
            window.open(meetUrl);
          } else if (attempt < MEET_POLL_MAX_ATTEMPTS) {
            message.loading({
              content: "Preparing your Google Meet link…",
              key: "meet-link-loading",
              duration: 0,
            });
            setTimeout(
              () => attemptJoin(userType, role, attempt + 1),
              MEET_POLL_INTERVAL_MS
            );
          } else {
            message.destroy("meet-link-loading");
            setIsJoining(false);
            message.error(
              "Your Google Meet link isn't ready yet. Please try again in a moment."
            );
          }
        } else {
          setIsJoining(false);
          window.open(
            handleMeetingLink(role, {
              token: data.data.bookingToken,
              roomId: roomId ?? "",
              firstName: userInfo?.user?.firstName ?? "",
              lastName: userInfo?.user?.lastName ?? "",
              meetingTitle: serviceName,
            })
          );
        }
      },
      onError: () => {
        message.destroy("meet-link-loading");
        setIsJoining(false);
      },
    });
  }

  function joinVideoCall() {
    if ((videoPlatform === "googleMeet" || isGoogleMeetLink(roomId)) && roomId) {
      window.open(roomId);
      return;
    }

    setIsJoining(true);
    attemptJoin("host_user", "host", 0);
  }

  return (
    <div>
      <Tooltip title={toolTipTitle}>
        <Button
          size="small"
          block
          type="primary"
          htmlType="button"
          onClick={bookingType === "video" ? joinVideoCall : copyPhoneNumber}
          loading={isJoining}
        >
          {buttonText}
        </Button>
      </Tooltip>
    </div>
  );
}

export function CustomerUpcomingCallJoinVideoCallButton({
  bookingType,
  phoneNumber,
  reference,
  roomId,
  serviceName,
  videoPlatform,
}: CoachUpcomingCallJoinVideoCallButtonProps) {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const getCustomerBookingQueryMutation = useGetCustomerBookingMutation();
  const [isJoining, setIsJoining] = useState(false);
  const toolTipTitle = bookingType === "video" ? "Join session" : "Copy phone number";
  const buttonText = bookingType === "video" ? "Join video call" : "Copy phone number";

  function copyPhoneNumber() {
    navigator.clipboard.writeText(`${phoneNumber}`).then(() => message.success("Copied"));
  }

  function attemptJoin(attempt: number) {
    const payload: TFetchCustomerBookingPayload = {reference, userType: "guest_user"};
    getCustomerBookingQueryMutation.mutate(payload, {
      onSuccess: (data) => {
        const platform = data.data.videoPlatform.toLowerCase();
        if (platform === "dailyco") {
          setIsJoining(false);
          window.open(`${data.data.dailyCoUrl}?t=${data.data.bookingToken}`);
        } else if (platform === "googlemeet") {
          const meetUrl = resolveGoogleMeetUrl(data, roomId);
          if (meetUrl) {
            message.destroy("meet-link-loading");
            setIsJoining(false);
            window.open(meetUrl);
          } else if (attempt < MEET_POLL_MAX_ATTEMPTS) {
            message.loading({
              content: "Preparing your Google Meet link…",
              key: "meet-link-loading",
              duration: 0,
            });
            setTimeout(() => attemptJoin(attempt + 1), MEET_POLL_INTERVAL_MS);
          } else {
            message.destroy("meet-link-loading");
            setIsJoining(false);
            message.error(
              "Your Google Meet link isn't ready yet. Please try again in a moment."
            );
          }
        } else {
          setIsJoining(false);
          window.open(
            handleMeetingLink("guest", {
              token: data.data.bookingToken,
              roomId: roomId ?? "",
              firstName: userInfo?.user?.firstName ?? "",
              lastName: userInfo?.user?.lastName ?? "",
              meetingTitle: serviceName,
            })
          );
        }
      },
      onError: () => {
        message.destroy("meet-link-loading");
        setIsJoining(false);
      },
    });
  }

  function joinVideoCall() {
    if ((videoPlatform === "googleMeet" || isGoogleMeetLink(roomId)) && roomId) {
      window.open(roomId);
      return;
    }

    setIsJoining(true);
    attemptJoin(0);
  }

  return (
    <div>
      <Tooltip title={toolTipTitle}>
        <Button
          size="small"
          block
          type="primary"
          htmlType="button"
          onClick={bookingType === "video" ? joinVideoCall : copyPhoneNumber}
          loading={isJoining}
        >
          {buttonText}
        </Button>
      </Tooltip>
    </div>
  );
}

export function CoachUpcomingCallViewDetailsButton({
  showViewDetailsDrawer,
}: CoachUpcomingCallViewDetailsButtonProps) {
  return (
    <div>
      <Button size="small" block type="default" onClick={showViewDetailsDrawer}>
        View details
      </Button>
    </div>
  );
}

export function UpcomingCallCardViewDetailsDrawer({
  title,
  isVisible,
  onClose,
  checkoutFormResponses,
  callMetaInfo,
  callLocation,
  videoPlatform,
  emailInfo,
  showCancelDrawer,
  showRescheduleCallDrawer,
  redirectUrl,
}: UpcomingCallCardViewDetailsDrawerProps) {
  console.log(
    "[ViewDetailsDrawer] callLocation:",
    callLocation,
    "videoPlatform:",
    videoPlatform
  );
  const locationLabel =
    callLocation !== "video"
      ? "Phone call"
      : videoPlatform === "googleMeet"
      ? "Google Meet"
      : videoPlatform === "zoom"
      ? "Zoom"
      : "Coachli Video";
  return (
    <StyledSessionDetailsDrawer
      title={title}
      placement="right"
      onClose={onClose}
      visible={isVisible}
      closable={false}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
      footer={
        <StyledCardCTAButtonWrapper>
          <div>
            <Button
              size="small"
              block
              type="primary"
              htmlType="button"
              onClick={showRescheduleCallDrawer}
            >
              Reschedule call
            </Button>
          </div>
          <div>
            <Button size="small" block htmlType="button" onClick={showCancelDrawer}>
              Cancel call
            </Button>
          </div>
        </StyledCardCTAButtonWrapper>
      }
    >
      <div>
        <Space direction="vertical" style={{display: "flex"}} size={16}>
          <UpcomingCallCardMetaInfo data={callMetaInfo} />
          {redirectUrl && (
            <div>
              <p>
                Your purchase includes a link:{" "}
                <a
                  href={redirectUrl.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{fontWeight: 600, textDecoration: "underline"}}
                >
                  {redirectUrl.name}
                </a>
              </p>
            </div>
          )}
          <p>
            {emailInfo.emailText}:{" "}
            <span>
              <b>{emailInfo.emailValue}</b>
            </span>
          </p>
          <p>
            Location:{" "}
            <span>
              <b>{locationLabel}</b>
            </span>
          </p>
          <SessionDetailsDrawerBookingQuestions data={checkoutFormResponses} />
        </Space>
      </div>
    </StyledSessionDetailsDrawer>
  );
}

export function UpcomingCallCardCancelCallDrawer({
  isVisible,
  nameOfPerson,
  onClose,
  title,
  bookingReference,
  type,
}: UpcomingCallCardCancelCallDrawerProps) {
  return (
    <StyledSessionDetailsDrawer
      title={
        <Breadcrumb separator="">
          <Breadcrumb.Item onClick={onClose}>
            <span
              style={{
                fontSize: "1.8rem",
                fontWeight: "600",
                cursor: "pointer",
                maxWidth: "30rem",
              }}
            >
              {title}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Breadcrumb.Separator />
            <span style={{fontSize: "2rem", fontWeight: 400}}>Cancel call</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      }
      closable={false}
      visible={isVisible}
      onClose={onClose}
      placement="right"
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
    >
      <Space direction="vertical" style={{display: "flex"}} size={16}>
        <DrawerInfoBlock nameOfPerson={nameOfPerson} />
        <UpcomingCallCardCancelCallDrawerCancelForm
          closeCancelCallDrawer={onClose}
          bookingReference={bookingReference}
          type={type}
        />
      </Space>
    </StyledSessionDetailsDrawer>
  );
}

export function DrawerInfoBlock({nameOfPerson}: DrawerInfoBlockProps) {
  return (
    <StyleDrawerInfoBlock>
      <p>
        We will notify <b>{`${nameOfPerson}`}</b> to inform them of your desired change to
        this call
      </p>
    </StyleDrawerInfoBlock>
  );
}
export function UpcomingCallCardCancelCallDrawerCancelForm({
  closeCancelCallDrawer,
  bookingReference,
  type,
}: UpcomingCallCardCancelCallDrawerCancelFormProps) {
  const {control, handleSubmit, watch, formState, reset, setError} =
    useFormContext<CancelCallFormValues>();
  const coachCancelUpcomingCallMutation = useCoachCancelUpcomingCall();
  const customerCancelUpcomingCallMutation = useCustomerCancelUpcomingCall();
  function onSubmit(values: CancelCallFormValues) {
    if (type === "coach") {
      const payload: CoachCancelUpcomingCallPayload = {
        bookingReference,
        reasonForCancellation:
          values.message === "" ? "No reason provided" : values.message,
      };
      coachCancelUpcomingCallMutation.mutate(payload, {
        onSuccess() {
          reset();
          closeCancelCallDrawer();
          message.success("The call has been cancelled successfully");
        },
      });
    } else {
      if (values.message === "") {
        setError("message", {
          message: "Reason for cancellation is required",
        });
      } else {
        const payload: CustomerCancelUpcomingCallPayload = {
          bookingReference,
          reasonForCancellation: values.message,
        };
        customerCancelUpcomingCallMutation.mutate(payload, {
          onSuccess() {
            reset();
            closeCancelCallDrawer();
            message.success("The call has been cancelled successfully");
          },
        });
      }
    }
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Space direction="vertical" size={16} style={{display: "flex"}}>
        <FormField
          id={"cancel-call"}
          label={"Reasons for cancellation"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="message"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={16} style={{display: "flex"}}>
                  <TextArea
                    rows={4}
                    placeholder="Let them know why you are cancelling this call"
                    onChange={onChange}
                    value={value}
                    ref={ref}
                    onBlur={onBlur}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        />
        <Button
          type="primary"
          htmlType={"submit"}
          size="small"
          loading={
            coachCancelUpcomingCallMutation.isLoading ||
            customerCancelUpcomingCallMutation.isLoading
          }
          disabled={
            Object.keys(formState.errors).length > 0 || Object.values(watch()).length < 1
          }
        >
          Cancel
        </Button>
      </Space>
    </form>
  );
}

export function UpcomingCallCardRescheduleCallDrawer({
  isVisible,
  nameOfPerson,
  onClose,
  title,
  bookingReference,
  serviceId,
  mentorTimezone,
  coachUsername,
  type,
}: UpcomingCallCardRescheduleCallDrawerProps) {
  const {dispatch} = useBookingContext();
  const {userInfo} = useSelector((state: RootState) => state.userLogin);

  // The availability endpoints key off the coach's numeric id, and no v2 booking
  // route hands it over: both join routes answer with {token, roomId,
  // videoPlatform} only, and the upcoming-calls rows carry serviceId but no coach
  // id. So each actor resolves it from what it already holds - the coach *is* the
  // mentor, and the customer's row names the coach by username, which
  // public/profile turns into an id. Same lookup the purchased-bundles calendar
  // does, and for the same reason.
  const coachProfileQuery = useGetPublicCoachProfile(
    type === "customer" ? coachUsername ?? null : null
  );
  const mentorId =
    type === "coach" ? userInfo?.user?.id ?? 0 : coachProfileQuery.data?.userId ?? 0;

  // isInitialLoading, not isLoading: the profile query is disabled on the coach
  // dashboard, and a disabled query reports isLoading forever.
  if (coachProfileQuery.isInitialLoading) {
    return <FullPageLoader />;
  }
  if (coachProfileQuery.isError || !mentorId || !serviceId) {
    return (
      <Alert
        message="Error"
        description={
          coachProfileQuery.error?.message ??
          "We couldn't load this coach's availability."
        }
        type="error"
        showIcon
      />
    );
  }
  return (
    <StyledSessionDetailsDrawer
      title={
        <Breadcrumb separator="">
          <Breadcrumb.Item onClick={onClose}>
            <span
              style={{
                fontSize: "1.8rem",
                fontWeight: "600",
                cursor: "pointer",
                maxWidth: "30rem",
              }}
            >
              {title}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Breadcrumb.Separator />
            <span style={{fontSize: "2rem", fontWeight: 400}}>Reschedule call</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      }
      closable={false}
      destroyOnClose
      visible={isVisible}
      onClose={() => {
        dispatch({
          type: "reset-state",
        });
        onClose();
      }}
      placement="right"
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => {
            dispatch({
              type: "reset-state",
            });
            onClose();
          }}
        ></Button>
      }
    >
      <Space direction="vertical" style={{display: "flex"}} size={16}>
        <DrawerInfoBlock nameOfPerson={nameOfPerson} />
        <UpcomingCallCardRescheduleCallDrawerRescheduleForm />
        {type === "coach" ? (
          <CoachRescheduleCallCalendar
            mentorId={mentorId}
            serviceId={serviceId}
            bookingReference={bookingReference}
            coachTimezone={mentorTimezone ?? null}
            onCloseRescheduleCallModalDrawer={onClose}
          />
        ) : (
          <CustomerRescheduleCallCalendar
            mentorId={mentorId}
            serviceId={serviceId}
            bookingReference={bookingReference}
            coachTimezone={mentorTimezone ?? null}
            onCloseRescheduleCallModalDrawer={onClose}
          />
        )}
      </Space>
    </StyledSessionDetailsDrawer>
  );
}

export function UpcomingCallCardRescheduleCallDrawerRescheduleForm() {
  const {control} = useFormContext<RescheduleCallFormValues>();
  return (
    <Space direction="vertical" size={16} style={{display: "flex"}}>
      <FormField
        id={"reschedule-call"}
        label={"Reasons for rescheduling"}
        renderInput={(id) => (
          <Controller
            control={control}
            name="message"
            render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
              <Space direction="vertical" size={16} style={{display: "flex"}}>
                <TextArea
                  rows={4}
                  placeholder="Let them know why you are rescheduling this call"
                  onChange={onChange}
                  value={value}
                  ref={ref}
                  onBlur={onBlur}
                />
                <FormFieldError errorMessage={error?.message} />
              </Space>
            )}
          />
        )}
      />
    </Space>
  );
}

export function UpcomingCallCardMetaInfo({data}: UpcomingCallCardMetaInfoProps) {
  return (
    <StyledUpcomingCallCardMetaInfo>
      {data.map((d, index) => (
        <Space
          key={d.value + index}
          size={10}
          direction="horizontal"
          style={{display: "flex"}}
        >
          {d.icon}
          <span>{d.value}</span>
        </Space>
      ))}
    </StyledUpcomingCallCardMetaInfo>
  );
}
